/**
 * Number of digits to show at the end of masked phone numbers
 */
export const MASKED_DIGITS_COUNT = 3;

/**
 * Masks a phone number showing only the last N digits
 * @param phoneNumber - The full phone number to mask
 * @param digitsToShow - Number of digits to show at the end (default: 3)
 * @returns Masked phone number (e.g., "•••••••123") or "—" if no number provided
 */
export const maskPhoneNumber = (
  phoneNumber: string | null | undefined,
  digitsToShow: number = MASKED_DIGITS_COUNT
): string => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return "—";
  }

  const cleanNumber = phoneNumber.trim();
  
  if (cleanNumber.length <= digitsToShow) {
    // If the number is shorter than or equal to the digits to show,
    // just show the whole number (edge case, but safer than showing nothing)
    return cleanNumber;
  }

  const lastDigits = cleanNumber.slice(-digitsToShow);
  const maskedPart = "•".repeat(cleanNumber.length - digitsToShow);
  
  return `${maskedPart}${lastDigits}`;
};

/**
 * Formats a date string to UK format (dd/mm/yyyy, hh:mm:ss)
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in UK format
 */
export const formatDateTimeUK = (dateString: string | Date): string => {
  const date = new Date(dateString);
  
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

/**
 * Parses a UK-formatted date string back to a timestamp for sorting
 * @param dateTimeStr - Date string in UK format (dd/mm/yyyy, hh:mm:ss)
 * @returns Timestamp in milliseconds
 */
export const parseDateTime = (dateTimeStr: string): number => {
  const [datePart, timePart] = dateTimeStr.split(", ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours, minutes, seconds] = timePart.split(":").map(Number);

  return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
};

/**
 * Maps raw message data from GraphQL to table row format
 * @param messages - Array of messages from GraphQL query
 * @returns Array of formatted table rows
 */
export const mapMessagesToTableData = (
  messages: Array<{
    MessageId: any;
    CreatedOn: string;
    Organisation: { Name: any };
    SendFailureReason: string;
    ExternalId: string;
    Conversation: {
      Contact: { MobileNumber: string } | null;
      Folder: { Inbox: { Name: string } | null } | null;
    } | null;
  }>
): Array<{
  Id: number;
  CreatedOn: string;
  OrgName: string;
  Inbox: string;
  Contact: string;
  SendFailureReason: string;
  ExternalId: string;
}> => {
  return messages.map((item) => ({
    Id: item.MessageId,
    CreatedOn: formatDateTimeUK(item.CreatedOn),
    OrgName: item.Organisation.Name,
    Inbox: item.Conversation?.Folder?.Inbox?.Name || "—",
    Contact: maskPhoneNumber(item.Conversation?.Contact?.MobileNumber),
    SendFailureReason: item.SendFailureReason,
    ExternalId: item.ExternalId,
  }));
};
